import React from 'react'
import classNames from 'classnames'
import { OverlayTrigger } from 'react-bootstrap'
import { Button } from '../../../../components/Button/Button'
import { type CrossClientAction, CrossClientActionPhases } from '../../../../models/CrossClient'
import { useAppSelector } from '../../../../store/hooks'
import { selectCrossClientActionsByClientId } from '../../../../store/crossClient/crossClientActions'
import { crossClientActionLogPopover } from '../../Actions/CrossClientActionLogPopover'
import { CcqAiIndicator } from './CcqAiIndicator'
import { CCQ_AI_CHECKING_TAG, ccqActionCount, ccqActionLabel } from './ccqRunCopy'
import { useCcqAi } from './useCcqAi'
import styles from './CcqGroupCard.module.scss'
import { CCQ_BADGE_CLASS } from './ccqClassNames'

const TAG_CLASS = classNames(CCQ_BADGE_CLASS, 'd-inline-flex align-items-center gap-1 text-nowrap flex-shrink-0')

const EMPTY_ACTIONS: CrossClientAction[] = []

interface CcqActionTagProps {
  clientId: string
  clientName: string
}

export const CcqActionTag: React.FC<CcqActionTagProps> = ({ clientId, clientName }) => {
  const actions = useAppSelector(selectCrossClientActionsByClientId)[clientId] ?? EMPTY_ACTIONS
  if (actions.length === 0) {
    return null
  }
  const running = actions.filter((action) => action.phase !== CrossClientActionPhases.COMPLETED).length

  return (
    <OverlayTrigger trigger='click' rootClose placement='bottom-start' overlay={crossClientActionLogPopover({ actions, clientId, clientName })}>
      <Button type='button' className={classNames(TAG_CLASS, 'py-0 zocks-text-gray-900')} onClick={(event) => event.stopPropagation()}>
        {running > 0 ? <span className={classNames(styles.actionDot, 'rounded-circle flex-shrink-0')} aria-hidden='true' /> : null}
        <span aria-hidden='true'>{ccqActionCount(actions.length)}</span>
        <span className='visually-hidden'>{ccqActionLabel(actions.length, running)}</span>
      </Button>
    </OverlayTrigger>
  )
}

interface CcqActionTagSlotProps {
  resultId: string
  clientId: string
  clientName: string
  isAiChecking: boolean
}

// Reserved whether or not there are any tags, so rows line up and nothing shifts when one appears.
export const CcqActionTagSlot: React.FC<CcqActionTagSlotProps> = ({ resultId, clientId, clientName, isAiChecking }) => {
  const { isChecking } = useCcqAi()

  return (
    <div className={classNames(styles.actionSlot, 'd-flex align-items-center justify-content-end gap-1 flex-shrink-0')}>
      {isAiChecking || isChecking(resultId) ? (
        <span className={classNames(TAG_CLASS, 'zocks-text-gray-900')}>
          <CcqAiIndicator />
          {CCQ_AI_CHECKING_TAG}
        </span>
      ) : (
        <CcqActionTag clientId={clientId} clientName={clientName} />
      )}
    </div>
  )
}
